import type { HTMLAttributes } from 'react';
import { forwardRef } from 'react';

import styles from './render-svg.module.css';
import iconMapping from './svg-mapping.json';

export type SvgMapping = keyof typeof iconMapping;
export const iconTypes = Object.keys(iconMapping) as SvgMapping[];
/**
 * An icon is either decorative or meaningful, never both:
 *
 * - **Decorative** (the default): omit `aria-label` and `role`. The icon is
 *   rendered `aria-hidden`, so assistive technology skips it entirely and the
 *   surrounding text or the parent control carries the accessible name.
 * - **Meaningful**: pass both `aria-label` and `role` (`role="img"` for content,
 *   `role="status"`/`role="alert"` for state). The icon then contributes its own
 *   name to the accessibility tree.
 */
export type SvgProps = HTMLAttributes<HTMLSpanElement> & {
  name: SvgMapping;
  color?: string;
  width?: number | `${number}%`;
  height?: number | `${number}%`;
  outerSvgSize?: number;
  innerSvgSize?: number;
} & (
    | {
        'aria-label'?: never;
        role?: never;
      }
    | {
        'aria-label': string;
        role: HTMLAttributes<Element>['role'];
      }
  );

const numberToStringPixel = (
  value: SvgProps['width'] | SvgProps['height'] | string
) => (typeof value === 'string' ? value : `${value}px`);

export const RenderSvg = forwardRef<HTMLSpanElement, SvgProps>((props, ref) => {
  const {
    role = 'presentation',
    color,
    className: additionalClassName,
    name,
    outerSvgSize,
    innerSvgSize,
    height = 40,
    width = 40,
    style: additionalStyle,
    ...rest
  } = props;

  const cssVars = {
    color,
    '--render-svg-icon-color': color ?? 'currentcolor',
    '--render-svg-icon-url': `url('${iconMapping[name].url}?key=v1')`,
    '--render-svg-height': numberToStringPixel(outerSvgSize || height),
    '--render-svg-width': numberToStringPixel(outerSvgSize || width),
    '--render-svg-background-size': innerSvgSize
      ? numberToStringPixel(innerSvgSize)
      : 'contain',
  };

  // Decorative unless the caller opts in with an `aria-label`. `role` alone is
  // not enough: the union type only permits it alongside a label.
  const isDecorative = !props['aria-label'];

  return (
    <span
      ref={ref}
      style={{ ...cssVars, ...additionalStyle }}
      className={`${styles.svg} ${iconMapping[name].canBeColoured && styles.coloured} ${additionalClassName}`}
      role={role}
      aria-hidden={isDecorative || undefined}
      {...rest}
    />
  );
});
